import { useContext, useRef } from "react";
import { ThemeContext } from "../../../themes/Theme";
import styles from "./AppetiteIcon.module.css";
import React from "react";
import { useClientId } from "../../../ClientConfig";
import * as client from "../../../client";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { perClient } from "../../../client-features";
import {
  numericAppetiteScore,
  readableAppetiteLong,
  readableAppetiteShort,
} from "../dashboard/util";
import { OverlayPanel } from "primereact/overlaypanel";
import { Button } from "primereact/button";
import { ROUTES } from "../../../routes";

type AppetiteIconProps = {
  naicsInfo: client.NaicsInfo;
  precomputedMetadata: client.PrecomputedMetadata;
  status: client.status;
  size?: "small" | "large" | "full_width";
  fontSize?: string;
  justifyContent?: string;
};

const colorForAttributes = (
  appetite: number,
  isLightMode: boolean,
  isDeclinedStatus: boolean
) => {
  if (isDeclinedStatus) {
    return isLightMode ? "var(--gray-100)" : "#11182744";
  }

  switch (appetite) {
    case 5:
      return isLightMode ? "var(--green-100)" : "#0e4f2644";
    case 4:
      return isLightMode ? "var(--cyan-100)" : "#18346244";
    case 3:
      return isLightMode ? "var(--yellow-100)" : "#5e480344";
    case 2:
      return isLightMode ? "var(--orange-100)" : "#642e0944";
    case 1:
      return isLightMode ? "var(--red-100)" : "#66181444";
  }
};

const borderColorForAttributes = (
  appetite: number,
  isLightMode: boolean,
  isDeclinedStatus: boolean
) => {
  if (isDeclinedStatus) {
    return isLightMode ? "var(--gray-400)" : "var(--gray-700)";
  }

  let value = "";
  switch (appetite) {
    case 5:
      value = isLightMode ? "var(--green-400)" : "var(--green-700)";
      break;
    case 4:
      value = isLightMode ? "var(--cyan-400)" : "var(--cyan-700)";
      break;
    case 3:
      value = isLightMode ? "var(--yellow-400)" : "var(--yellow-700)";
      break;
    case 2:
      value = isLightMode ? "var(--orange-400)" : "var(--orange-700)";
      break;
    case 1:
      value = isLightMode ? "var(--red-400)" : "var(--red-700)";
      break;
      break;
  }

  return value;
};

const AppetiteIconLuminor: React.FC<AppetiteIconProps> = ({
  size = "small",
  fontSize = "12px",
  justifyContent = "center",
  precomputedMetadata,
  status,
}) => {
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const overlayPanel = useRef<OverlayPanel>(null);

  if (!precomputedMetadata.appetite) {
    return <></>;
  }

  const hasMatchingReport =
    (precomputedMetadata.matching_reports_by_addresses?.matching_report_info
      .length ?? 0) > 0;

  const appetiteResults: client.appetite_result[] = precomputedMetadata.appetite
    .appetite_results ?? [precomputedMetadata.appetite.appetite_result];

  let tooltipText = appetiteResults
    .filter((r): r is client.appetite_result => r !== "in_appetite")
    .map((r) => readableAppetiteLong(r))
    .filter((t): t is string => !!t)
    .join("\n\n");

  if (hasMatchingReport) {
    if (tooltipText) {
      tooltipText += "\n\n";
    }
    tooltipText +=
      "This submission has a location overlap with another submission. Click the top right badge to learn more.";
  }

  const shouldShowTooltip =
    precomputedMetadata.appetite.appetite_result !== "in_appetite" ||
    hasMatchingReport;

  return (
    <TooltipWrapper
      tooltipText={tooltipText}
      tooltipVisible={shouldShowTooltip}
    >
      <div
        className={styles.iconContainer}
        style={{
          width:
            size === "small" ? "132px" : size === "large" ? "170px" : "100%",
          height: size === "small" ? "22px" : "32px",
          backgroundColor: colorForAttributes(
            numericAppetiteScore(precomputedMetadata.appetite.appetite_result),
            isLightMode,
            false
          ),
          borderColor: borderColorForAttributes(
            numericAppetiteScore(precomputedMetadata.appetite.appetite_result),
            isLightMode,
            false
          ),
          borderRadius: size === "small" ? "4px" : "6px",
          fontSize: fontSize,
          justifyContent: justifyContent,
        }}
      >
        {hasMatchingReport && (
          <>
            <Button
              raised
              className={styles.badgeButton}
              onClick={(e) => {
                if (overlayPanel.current) {
                  overlayPanel.current.toggle(e);
                }
              }}
              label={`${
                precomputedMetadata.matching_reports_by_addresses
                  ?.matching_report_info.length ?? ""
              }`}
              severity={
                status === "declined" || status === "closed"
                  ? "secondary"
                  : "info"
              }
            />
            <OverlayPanel ref={overlayPanel} showCloseIcon>
              <div
                style={{ fontWeight: "bold", fontSize: "18px" }}
                className={styles.matchingReportsContainer}
                onClick={(e) => e.stopPropagation()}
              >
                Matching Submissions
                {(
                  precomputedMetadata.matching_reports_by_addresses
                    ?.matching_report_info ?? []
                ).map((reportInfo) => {
                  return (
                    <div
                      key={reportInfo.report_id}
                      className={styles.matchingReportContainer}
                    >
                      <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                        {reportInfo.report_name}
                      </div>
                      <div
                        style={{
                          fontWeight: "normal",
                          fontSize: "12px",
                          color: "var(--text-color-secondary)",
                        }}
                      >
                        {reportInfo.matching_addresses.length} matching{" "}
                        {reportInfo.matching_addresses.length === 1
                          ? "address"
                          : "addresses"}
                        {reportInfo.matching_tiv != null &&
                          reportInfo.total_tiv != null &&
                          (() => {
                            const smallerTiv = Math.min(
                              precomputedMetadata.total_tiv,
                              reportInfo.total_tiv
                            );
                            if (smallerTiv <= 0) return null;
                            return (
                              <>
                                {" • "}
                                {(
                                  (reportInfo.matching_tiv / smallerTiv) *
                                  100
                                ).toFixed(1)}
                                % TIV overlap
                              </>
                            );
                          })()}
                      </div>
                      {reportInfo.matching_addresses
                        .slice(0, 3)
                        .map((address) => (
                          <div
                            key={address}
                            style={{
                              fontWeight: "normal",
                              fontSize: "12px",
                              marginLeft: "8px",
                            }}
                          >
                            {address}
                          </div>
                        ))}
                      <div>
                        <Button
                          link
                          label="Open"
                          size="small"
                          style={{ height: "20px", marginLeft: "-8px" }}
                          icon="pi pi-external-link"
                          iconPos="right"
                          onClick={() =>
                            window.open(
                              "#" +
                                ROUTES.DASHBOARD_REPORT(
                                  reportInfo.report_id,
                                  "review"
                                ),
                              "_blank"
                            )
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </OverlayPanel>
          </>
        )}
        <span className={styles.text}>
          {readableAppetiteShort(precomputedMetadata.appetite.appetite_result)}
        </span>
      </div>
    </TooltipWrapper>
  );
};

export const AppetiteIcon: React.FC<AppetiteIconProps> = ({
  naicsInfo,
  precomputedMetadata,
  status,
  size = "small",
  fontSize = "12px",
  justifyContent = "center",
}) => {
  const clientId = useClientId();
  return perClient({
    luminor: () => (
      <AppetiteIconLuminor
        naicsInfo={naicsInfo}
        status={status}
        precomputedMetadata={precomputedMetadata}
        size={size}
        fontSize={fontSize}
        justifyContent={justifyContent}
      />
    ),
  })[clientId]?.();
};
